// Prompt data for the record flow, read from <script id="oh-prompt-data"> on the page.
// The template builds that block ONLY through the publicPrompts / publicSessions filters, so
// interviewer-only prompts (Session 8) never reach this code. Prompts carry the self-record
// wording already (selfText || text), and sessions are shown by their self-record title
// (selfTitle || title, ARCHITECTURE.md section 11.2): some template titles are written from the
// interviewer's side ("Opening: who they are").
import { readJson, keepTitlesTogether as nb } from './dom.js';

export function loadPromptData() {
  const d = readJson('oh-prompt-data');
  if (!d || !Array.isArray(d.sessions) || !Array.isArray(d.prompts)) throw new Error('Prompt data missing from the page');

  const eraLabel = Object.fromEntries((d.eras || []).map((e) => [e.id, e.label]));
  // Display text keeps "St. Theresa" and the like on one line (no-break space after the title).
  const sessions = d.sessions.map((s) => ({ ...s, title: nb(s.title), selfTitle: s.selfTitle ? nb(s.selfTitle) : s.selfTitle, history: s.history ? nb(s.history) : s.history }));
  const prompts = d.prompts.map((p) => ({ ...p, text: nb(p.text) })).sort((a, b) => a.number - b.number);
  const open = {
    id: (d.openPrompt && d.openPrompt.id) || 'open',
    session: 'open',
    number: 1,
    text: nb((d.openPrompt && (d.openPrompt.selfText || d.openPrompt.text)) || 'Is there a story we did not ask about? Tell it here, in your own words.'),
    title: nb((d.openPrompt && d.openPrompt.text) || 'Something else you want remembered'),
  };
  const promptMap = new Map(prompts.map((p) => [p.id, p]));
  promptMap.set(open.id, open);
  const sessionMap = new Map(sessions.map((s) => [s.id, s]));
  const roles = d.roles || [];

  function promptsFor(sessionId, roleId) {
    return prompts.filter((p) => p.session === sessionId && (!roleId || roleId === 'all' || (p.roles || []).includes(roleId)));
  }

  /** The title a contributor sees for a session. */
  const titleOf = (s) => s.selfTitle || s.title;

  function eraFor(s) {
    if (s.kind !== 'session' || !s.era || s.era === 'all') return '';
    const label = eraLabel[s.era] || '';
    // Skip a label the title already says ("Launching the school, 1983 to 1986",
    // "Raj, Brooks and Covington, 2004 to today").
    return label && !String(titleOf(s)).toLowerCase().includes(label.toLowerCase()) ? label : '';
  }

  function toTopic(s, roleId) {
    return {
      id: s.id,
      kind: s.kind,
      title: titleOf(s),
      era: s.era,
      eraLabel: eraFor(s),
      history: s.history || null,
      suggested: s.kind === 'session' && s.number === 1,
      prompts: promptsFor(s.id, roleId),
    };
  }

  /**
   * Topics for a connection, in that role's path order from prompts.json (roles[].sessions),
   * then the lightning round, then the role's add-on questions, then the open prompt.
   * roleId 'all' (or unknown) lists every public topic.
   */
  function topicsFor(roleId) {
    const role = roles.find((r) => r.id === roleId);
    let list = [];
    if (role) {
      for (const n of role.sessions || []) {
        const s = sessions.find((x) => x.kind === 'session' && x.number === n);
        if (s) list.push(s); // numbers with no public session (interviewer-only) are skipped
      }
      list.push(...sessions.filter((s) => s.kind === 'lightning' && (s.roles || []).includes(roleId)));
      list.push(...sessions.filter((s) => s.kind === 'addon' && (s.roles || []).includes(roleId)));
    } else {
      list = [
        ...sessions.filter((s) => s.kind === 'session').sort((a, b) => a.number - b.number),
        ...sessions.filter((s) => s.kind === 'lightning'),
        ...sessions.filter((s) => s.kind === 'addon'),
      ];
    }
    const topics = list.map((s) => toTopic(s, role ? roleId : 'all')).filter((t) => t.prompts.length);
    topics.push({ id: 'open', kind: 'open', title: open.title, era: 'all', eraLabel: '', history: null, suggested: false, prompts: [open] });
    return topics;
  }

  function topicTitleFor(promptId) {
    const p = promptMap.get(promptId);
    if (!p) return '';
    if (p.session === 'open') return open.title;
    const s = sessionMap.get(p.session);
    return s ? titleOf(s) : '';
  }

  return {
    roles,
    openPromptId: open.id,
    roleById: (id) => roles.find((r) => r.id === id) || null,
    topicsFor,
    promptById: (id) => promptMap.get(id) || null,
    topicTitleFor,
    isOpenPrompt: (id) => id === open.id,
  };
}
